import React, { useState, useEffect } from "react";

import {
  Box,
  Typography,
  Button,
  Grid,
  Chip,
  Avatar,
  Divider,
} from "@mui/material";
import API_URL from "../../config";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import API_BASE_URL from "../../utils/api";
import "./BlogDetails.css";

const cleanQuillHtml = (html) => {
  return (
    html
      // Remove <p><span>&nbsp;</span></p>
      .replace(/<p>\s*<span[^>]*>(?:&nbsp;|\s)*<\/span>\s*<\/p>/g, "")
      // Remove <p><br></p>
      .replace(/<p><br><\/p>/g, "")
      // Remove spans with only &nbsp;
      .replace(/<span[^>]*>(?:&nbsp;|\s)*<\/span>/g, "")
      .trim()
  );
};
function wrapImagesWithCaptions(html = "") {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  const lower = (s) => (s || "").toLowerCase();
  const has = (el, cls) => !!el && el.classList && el.classList.contains(cls);
  const pickAlign = (img, container) => {
    const d = lower(img.getAttribute("data-align"));
    if (["left", "right", "center"].includes(d)) return d;

    const imgStyle = lower(img.getAttribute("style") || "");
    const mFloat = imgStyle.match(/float\s*:\s*(left|right)/i);
    if (mFloat) return mFloat[1];

    if (has(container, "ql-align-right")) return "right";
    if (has(container, "ql-align-center")) return "center";
    if (has(container, "ql-align-left")) return "left";

    const cStyle = lower(container?.getAttribute?.("style") || "");
    const mTA = cStyle.match(/text-align\s*:\s*(left|right|center)/i);
    if (mTA) return mTA[1];

    const legacyAlign = lower(container?.getAttribute?.("align") || "");
    if (["left", "right", "center"].includes(legacyAlign)) return legacyAlign;

    return "";
  };

  Array.from(doc.querySelectorAll("img")).forEach((img) => {
    if (img.closest("span.img-unit")) return;

    const container =
      img.closest("p, div, span, li, figure") || img.parentElement;

    let legacyText = "";
    const next = container?.nextElementSibling;
    if (
      next &&
      next.tagName === "P" &&
      next.classList.contains("img-caption")
    ) {
      legacyText = next.textContent || "";
    }
    const caption =
      img.getAttribute("data-caption") ||
      img.getAttribute("alt") ||
      legacyText ||
      "";

    const unit = doc.createElement("span");
    let explicitWidth = "";
    const inlineStyle = img.getAttribute("style") || "";
    const m = inlineStyle.match(/width\s*:\s*([0-9.]+)(px|%)/i);
    if (m) explicitWidth = m[1] + m[2];
    else if (img.hasAttribute("width"))
      explicitWidth = img.getAttribute("width") + "px";

    if (explicitWidth) {
      unit.style.width = explicitWidth;
      if (img.hasAttribute("width")) img.removeAttribute("width");
      const cleaned = inlineStyle.replace(/width\s*:\s*[^;]+;?/i, "").trim();
      if (cleaned) img.setAttribute("style", cleaned);
      else img.removeAttribute("style");
    }
    unit.className = "img-unit";
    const align = pickAlign(img, container);
    if (align) unit.classList.add(`align-${align}`);

    img.parentNode.insertBefore(unit, img);
    unit.appendChild(img);

    const s = img.getAttribute("style") || "";
    if (/float\s*:\s*(left|right)/i.test(s)) {
      img.setAttribute(
        "style",
        s.replace(/float\s*:\s*(left|right)\s*;?/i, "").trim()
      );
    }

    if (caption.trim()) {
      const cap = doc.createElement("span");
      cap.className = "img-caption";
      cap.textContent = caption.trim();
      unit.appendChild(cap);
    }

    if (legacyText && next && next.parentNode)
      next.parentNode.removeChild(next);
  });

  return doc.body.innerHTML;
}

const BlogDetails = () => {
  const navigate = useNavigate();

  const { id } = useParams();
  const [blog, setBlog] = useState(null);
  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/blogs/${id}`)
      .then((res) => setBlog(res.data))
      .catch(console.error);
  }, [id]);
  if (!blog) return <Typography> Loading...</Typography>;
  const cleanedContent = cleanQuillHtml(blog.content);
  const htmlWithCaptions = wrapImagesWithCaptions(cleanedContent);

  return (
    <Box
      sx={{
        width: "auto",

        padding: "0px 20px",
        margin: "0px auto",
        padding: { xs: "20px", sm: "30px", md: "1px 80px" },
        border: "none",
        backgroundColor: "#ffffff",
        borderRadius: "8px",
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Button variant="outlined" onClick={() => navigate("/blogs")}>
          {" "}
          Back to Blogs
        </Button>
      </Box>
      <Typography variant="h4" fontWeight={600} gutterBottom>
        {blog.title}
      </Typography>
      <hr />
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Avatar src={blog.author.profileImage} />
          <Box>
            <Typography variant="caption">Written by</Typography>
            <Typography variant="body1" fontWeight={500}>
              {blog.author.name}
            </Typography>
          </Box>
        </Box>
        <Box>
          <Typography variant="caption">Posted On</Typography>
          <Typography variant="body2">
            {new Date(blog.createdAt).toDateString()}
          </Typography>
        </Box>
      </Box>
      <Divider sx={{ my: 3 }} />
      <Box
        className="blog-body"
        sx={{
          typography: "body1",

          "& *": {
            lineHeight: 1.8,
          },
          "& .img-unit": {
            display: "inline-block",
            verticalAlign: "top",
            margin: "5px 0 8px 0",
            maxWidth: "100%",
          },
          "& .img-unit.align-left": { float: "left", margin: "5px 12px 8px 0" },
          "& .img-unit.align-right": {
            float: "right",
            margin: "5px 0 8px 12px",
          },
          "& .img-unit.align-center": {
            float: "none",
            display: "block",
            margin: "0 auto 12px",
          },

          "& .img-unit > img": {
            display: "block",
            width: "100%",
            height: "auto",
            borderRadius: "6px",
            float: "none !important",
            margin: 0,
          },

          "& .img-unit > .img-caption": {
            display: "block",
            width: "100%",
            boxSizing: "border-box",
            margin: "4px 0 0 0",
            textAlign: "center",
            fontStyle: "italic",
            fontSize: "0.9rem",
            color: "#6b7280",
            lineHeight: 1.25,
            overflowWrap: "anywhere",
            whiteSpace: "normal",
          },

          "&::after": { content: '""', display: "block", clear: "both" },

          "& h1, & h2, & h3": {
            marginTop: "24px",
            marginBottom: "16px",
          },
          "& p": {
            margin: "0 0 15px 0",
            padding: 0,
            lineHeight: 1.6,
          },
          "& p:empty": { display: "none" },
          "& br": {
            display: "none",
          },
          "& p:has(> span:only-child:empty)": {
            display: "none",
          },
          "& blockquote": {
            borderLeft: "4px solid #ddd",
            paddingLeft: "16px",
            fontStyle: "italic",
            color: "#666",
            marginBottom: "5px",
          },
          "& pre": {
            background: "#f6f8fa",
            padding: "16px",
            borderRadius: "6px",
            overflowX: "auto",
            marginBottom: "16px",
          },
          "@media (max-width:600px)": {
            "& .img-unit, & .img-unit.align-left, & .img-unit.align-right, & .img-unit.align-center":
              {
                float: "none !important",
                display: "block !important",
                width: "100% !important",
                maxWidth: "100% !important",
                margin: "0 0 14px 0 !important",
              },
            "& .img-unit > img": {
              width: "100% !important",
              height: "auto !important",
              display: "block !important",
            },
          },
        }}
        dangerouslySetInnerHTML={{ __html: htmlWithCaptions }}
      />
    </Box>
  );
};
export default BlogDetails;
